import { z, ZodIssueCode } from '@botpress/sdk'
import { Dependencies } from './types.js'
import { IntegrationRef, ParsedIntegration } from '../integrations/types.js'
import { ValidationErrors } from './validation-errors.js'
import { ValidationError } from './types.js'

// Integration reference schema
const integrationRefSchema = z.string().transform((val, ctx) => {
  // Match pattern: [workspace/]name@version
  const match = val.match(/^(?:([^/]+)\/)?([^@]+)@(.+)$/)

  if (!match) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: `Invalid integration version format: ${val}. Expected format: 'name@version' or 'workspace/name@version'`,
    })
    return z.NEVER
  }

  const [, workspace, name, version] = match

  return {
    workspace: workspace || undefined,
    name: name!,
    version: version!,
    fullName: workspace ? `${workspace}/${name}` : name!,
  }
})

// Version validation schema
const versionSchema = z.string().refine(
  (version) => {
    if (version === 'latest') return true
    const semverPattern = /^(\d+)\.(\d+)\.(\d+)(-[\w.]+)?(\+[\w.]+)?$/
    return semverPattern.test(version)
  },
  { message: "Version must be exact semver (e.g., 1.2.3) or 'latest'" }
)

export class IntegrationParser {
  /**
   * Parse an integration version string into its components
   * Examples:
   * - "slack@1.2.0" -> { name: 'slack', version: '1.2.0', fullName: 'slack' }
   * - "myworkspace/slack@latest" -> { workspace: 'myworkspace', name: 'slack', version: 'latest', fullName: 'myworkspace/slack' }
   */
  static parseIntegrationRef(versionString: string): IntegrationRef {
    const result = integrationRefSchema.safeParse(versionString)
    if (!result.success) {
      throw new Error(result.error.errors[0]?.message || 'Invalid integration version format')
    }
    return result.data
  }

  /**
   * Parse integrations from dependencies object
   */
  static parseIntegrations(dependencies: Dependencies): {
    integrations: ParsedIntegration[]
    errors: ValidationError[]
  } {
    const integrations: ParsedIntegration[] = []
    const errors: ValidationError[] = []

    if (!dependencies.integrations) {
      return { integrations, errors }
    }

    for (const [alias, value] of Object.entries(dependencies.integrations)) {
      try {
        // Parse the integration reference
        const ref = this.parseIntegrationRef(value.version)

        // Validate version format
        const versionResult = versionSchema.safeParse(ref.version)
        if (!versionResult.success) {
          errors.push(ValidationErrors.invalidVersionFormat(alias, ref.version))
        }

        integrations.push({
          alias,
          ref,
          enabled: value.enabled,
          configurationType: value.configurationType,
          config: value.config,
        })
      } catch (error) {
        errors.push(
          ValidationErrors.invalidDependenciesSyntax(
            `Invalid integration '${alias}': ${error instanceof Error ? error.message : String(error)}`
          )
        )
      }
    }

    return { integrations, errors }
  }

  /**
   * Check for duplicate integration names (different aliases pointing to same integration)
   */
  static checkDuplicates(integrations: ParsedIntegration[]): ValidationError[] {
    const errors: ValidationError[] = []
    const seen = new Map<string, string[]>()

    for (const integration of integrations) {
      const key = integration.ref.fullName
      const aliases = seen.get(key) || []
      aliases.push(integration.alias)
      seen.set(key, aliases)
    }

    // Report duplicates
    for (const [integration, aliases] of seen.entries()) {
      if (aliases.length > 1) {
        errors.push(
          ValidationErrors.warning(
            `Integration '${integration}' is used with multiple aliases: ${aliases.join(', ')}. This is valid but ensure each has different configurations.`,
            'dependencies.json'
          )
        )
      }
    }

    return errors
  }
}
